import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { ScrollArea } from "../../ui/scroll-area";

import {
  Box,
  Database,
  Server,
  Key,
  Lock,
  Layers,
  FileText,
  HardDrive,
  Network,
  Container,
  Search,
  ChevronRight,
  ArrowLeft,
  ChevronLeft,
  Info,
  X,
  SettingsIcon,
  Shield,
  Clock,
  Cloud,
  Globe,
  Gauge,
} from "lucide-react";
import { SiKubernetes } from "react-icons/si";
import RequestCloudAccessDialog from "./RequestCloudAccessDialog";
import { DetailsDialog } from "../Cloud/DetailsDialog";
import { CloudUsersPageProps } from "../Cloud/interface";
import useNotificationsStore from "../../../services/notificationStore";
import { formatTime, getAzureKubernetesList, getAzureUserConfig, getGCPKubernetesList, getGCPUserConfig, getK8ApplicableRoles, getK8Resources, getK8ResourceTypes } from "./Data";
import useUserStore from "../../../services/userStore";
import { useNavigate } from "react-router-dom";

const ITEMS_PER_PAGE = 12;


const resourceTypesConstants = [
  { id: "namespace", name: "Namespace", icon: Layers },
  { id: "secret", name: "Secret", icon: Lock },
  { id: "node", name: "Node", icon: Server },
  { id: "serviceaccount", name: "Service Account", icon: Key },
  { id: "deployment", name: "Deployment", icon: Container },
  { id: "pod", name: "Pod", icon: Box },
  { id: "service", name: "Service", icon: Network },
  { id: "config_map", name: "ConfigMap", icon: SettingsIcon },
  { id: "PERSISTENT_VOLUME", name: "Persistent Volume", icon: HardDrive },
  { id: "STORAGE_CLASS", name: "Storage Class", icon: Database },
  { id: "NETWORK_POLICY", name: "Network Policy", type: "NETWORK_POLICY", icon: Shield },
  { id: "Job", name: "Job", type: "JOB", icon: FileText },
  { id: "CRON_JOB", name: "Cron Job", type: "CRON_JOB", icon: Clock },
  { id: "REPLICA_SET", name: "Replica Set", type: "REPLICA_SET", icon: Layers },
  { id: "STATEFUL_SET", name: "Stateful Set", type: "STATEFUL_SET", icon: Cloud },
  { id: "DAEMON_SET", name: "Daemon Set", type: "DAEMON_SET", icon: Gauge },
  { id: "INGRESS", name: "Ingress", type: "INGRESS", icon: Globe },
];



const getStatusColor = (status: string) => {
  switch (status) {
    case "running":
      return "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20";
    case "available":
      return "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20";
    case "pending":
      return "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const getResourceTypeLabel = (type: string) => {
  switch (type) {
    case "namespace":
      return "Kubernetes Namespace";
    case "secret":
      return "Kubernetes Secret";
    case "node":
      return "Kubernetes Node";
    case "serviceaccount":
      return "Kubernetes Service Account";
    case "deployment":
      return "Kubernetes Deployment";
    case "pod":
      return "Kubernetes Pod";
    case "service":
      return "Kubernetes Service";
    case "configmap":
      return "Kubernetes ConfigMap";
    case "persistentvolume":
      return "Kubernetes Persistent Volume";
    case "storageclass":
      return "Kubernetes Storage Class";
    default:
      return "Kubernetes";
  }
};

export default function TenantKubernetes({ cloudProvider }: CloudUsersPageProps) {
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [selectedResourceType, setSelectedResourceType] = useState<string>("NAMESPACE");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeSearchQuery, setTypeSearchQuery] = useState("");
  const [resourceSearchQuery, setResourceSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [clusterPage, setClusterPage] = useState(1);
  const [requestAccessDialogOpen, setRequestAccessDialogOpen] = useState(false);
  const [requestAccessResource, setRequestAccessResource] = useState<any>(null);
  const [clusters, setClusters] = useState<any[]>([]);
  const [resourceTypes, setResourceTypes] = useState<any[]>([]);
  const [currentResources, setCurrentResources] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const { email } = useUserStore((state) => state.user);
  const [upn, setUpn] = useState("");
  const [displayName, setDisplayName] = useState("");
  const navigate = useNavigate();
  const [resourceLoading, setResourceLoading] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(30 * 60);
  const {
    userAzureApprovedNotifications,
    userGCPApprovedNotifications,
    userAWSApprovedNotifications,
    userK8ApprovedNotifications
  } = useNotificationsStore();
  // Filter clusters by search
  const filteredClusters = clusters.filter((cluster) =>
    cluster.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cluster.region.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Cluster pagination
  const totalClusterPages = Math.ceil(filteredClusters.length / ITEMS_PER_PAGE);
  const clusterStartIndex = (clusterPage - 1) * ITEMS_PER_PAGE;
  const paginatedClusters = filteredClusters.slice(clusterStartIndex, clusterStartIndex + ITEMS_PER_PAGE);

  // Filter resource types by search
  const filteredResourceTypes = resourceTypes?.filter((type) => {
    const name = type?.name?.toLowerCase?.() || "";
    const query = typeSearchQuery?.toLowerCase?.() || "";
    return name.includes(query);
  });


  const currentCluster = clusters.find(c => c.id === selectedCluster);


  const searchFilteredResources = currentResources?.filter((resource) =>
    resource.name.toLowerCase().includes(resourceSearchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(searchFilteredResources.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResources = searchFilteredResources.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  // Reset pagination when filters change
  const handleResourceTypeChange = (typeId: string) => {
    setSelectedResourceType(typeId);
    setCurrentPage(1);
    setResourceSearchQuery("");
  };

  const handleResourceSearch = (query: string) => {
    setResourceSearchQuery(query);
    setCurrentPage(1);
  };

  const handleClusterSearch = (query: string) => {
    setSearchQuery(query);
    setClusterPage(1);
  };



  const fetchAccessGranted = (type: string, resource: any) => {
    console.log(userK8ApprovedNotifications, resource)
    let clusterResource = userK8ApprovedNotifications?.filter(
      (userK8ApprovedNotification: any) =>
        userK8ApprovedNotification.clusterName === currentCluster.name &&
        userK8ApprovedNotification.policyResourceName === resource.name &&
        userK8ApprovedNotification.status === "APPROVED");
    return clusterResource && clusterResource
  }


  const getUserDetails = useCallback(async () => {
    if (currentCluster && selectedResourceType) {
      if (cloudProvider === "gcp") {
        let response = await getGCPUserConfig(email);
        if (response) {
          let { gcpUserName, gcpAccountEmail } = response;
          if (gcpUserName) {
            setUpn(gcpAccountEmail);
            setDisplayName(gcpUserName);
          } else {
            setUpn("")
            setDisplayName("")
          }
        } else {

        }
      } else if (cloudProvider === "aws") {
      } else {
        try {
          let response = await getAzureUserConfig(email);
          if (response) {
            let { azureUserUpn, displayName } = response;
            setUpn(azureUserUpn);
            setDisplayName(displayName);
          } else {
            setUpn("");
            setDisplayName("");
          }
        } catch (e) {
          setUpn("");
          setDisplayName("");
        }

      }
    }
  }, [cloudProvider, selectedCluster, currentCluster])

  const getResources = useCallback(async () => {
    if (currentCluster && selectedResourceType) {
      setResourceLoading(true);
      let details = currentCluster.details
      let clusterId = cloudProvider === "aws" ? details.clusterArn : cloudProvider === "gcp" ? details.gcpClusterId : details.azureId
      let clusterName = details.name;
      let response = await getK8Resources(clusterId, selectedResourceType, cloudProvider);
      setCurrentResources(response)
      setResourceLoading(false)
    }

  }, [selectedCluster, cloudProvider, currentCluster, selectedResourceType])

  const getCurrentCluster = useCallback(async () => {
    if (cloudProvider === "gcp") {
      const fetchResourceLevel = userGCPApprovedNotifications?.filter(
        (userAWSNotification: any) =>
          userAWSNotification.accessType === "resources" &&
          userAWSNotification.requestStatus === "APPROVED",
      );
      const clusterIds = [
        ...new Set(fetchResourceLevel.map((item: any) => item.accessName)),
      ];
      const response = await getGCPKubernetesList();
      const clusterDetails = response.filter((item: any) =>
        clusterIds.includes(item.name),
      );
      setClusters(clusterDetails);
    } else if (cloudProvider === "aws") {
      const fetchResourceLevel = userAWSApprovedNotifications?.filter(
        (userAWSNotification: any) =>
          userAWSNotification.accessType === "resources" &&
          userAWSNotification.requestStatus === "APPROVED",
      );
      const clusterIds = [
        ...new Set(fetchResourceLevel.map((item: any) => item.accessName)),
      ];


      setClusters([]);
    } else {
      const fetchResourceLevel = userAzureApprovedNotifications?.filter(
        (userAzureNotification: any) =>
          userAzureNotification.scopeType === "AZURE_KUBERNETES" &&
          userAzureNotification.status === "APPROVED",
      );
      const clusterIds = [
        ...new Set(fetchResourceLevel.map((item: any) => item.scope)),
      ];
      const response = await getAzureKubernetesList();
      const clusterDetails = response.filter((item) =>
        clusterIds.includes(item.azureId),
      );
      setClusters(clusterDetails);
    }
  }, [cloudProvider]);


  useEffect(() => {
    const timer = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 0) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);


  useEffect(() => {

    const fetchCluster = async () => {
      try {
        const resources = await getCurrentCluster();
        //
      } catch (error) {
        console.error("Error fetching project resources:", error);
      }
    };

    fetchCluster();

  }, [cloudProvider])


  useEffect(() => {

    if (requestAccessDialogOpen && requestAccessResource) {
      const fetchApplicableRoles = async () => {
        try {

          const payload = {
            wsTenantName: requestAccessResource.wsTenantName,
            clusterId: requestAccessResource.clusterId,
            cloudProviderType: requestAccessResource.cloudProviderType,
            type: selectedResourceType,
            resourceAccountId: requestAccessResource.cloudResourceAccountId,
            namespace: requestAccessResource.namespace,
          }

          let name = requestAccessResource.name;
          const resources: any[] = await getK8ApplicableRoles(name, payload);
          setAvailableRoles(resources);
        } catch (error) {
          console.error("Error fetching project resources:", error);
        }
      };
      fetchApplicableRoles();
    }


  }, [cloudProvider, requestAccessDialogOpen, selectedResourceType, requestAccessResource]);


  useEffect(() => {
    if (currentCluster && selectedResourceType) {
      const fetchClusterResources = async () => {
        try {
          const resources = await getResources();
          //
        } catch (error) {
          console.error("Error fetching project resources:", error);
        }
      };

      fetchClusterResources();
    }

  }, [cloudProvider, selectedCluster, currentCluster, selectedResourceType])
  const iconMap = Object.fromEntries(
    resourceTypesConstants.map(({ id, icon }) => [id.toUpperCase(), icon])
  );
  const getResourceTypes = async () => {
    let response = await getK8ResourceTypes();
    console.log(response)
    const mergedResourceTypes = response.map((type: any) => ({
      ...type,
      icon: iconMap[type.key] || FileText, // fallback if icon not found
    }));
    console.log(mergedResourceTypes)
    setResourceTypes(mergedResourceTypes)
  }

  useEffect(() => {
    if (currentCluster) {
      getResourceTypes();
    }

  }, [cloudProvider, selectedCluster, currentCluster])

  useEffect(() => {
    if (currentCluster) {
      getUserDetails();
    }
  }, [cloudProvider, selectedCluster, currentCluster])


  // Cluster List View
  if (!selectedCluster) {
    return (
      <div className="h-full flex flex-col bg-background">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <SiKubernetes className="w-8 h-8 text-primary" />
            <div className="flex-1">
              <h1 className="text-2xl font-semibold text-foreground">Kubernetes Clusters</h1>
              <p className="text-sm text-muted-foreground">Manage and access your cluster resources</p>
            </div>
          </div>

          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search clusters by name, region, or group..."
                value={searchQuery}
                onChange={(e) => handleClusterSearch(e.target.value)}
                className="pl-10"
                data-testid="input-search-clusters"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {filteredClusters.length} {filteredClusters.length === 1 ? 'cluster' : 'clusters'}
            </div>
          </div>

          {/* Clusters Grid */}
          {filteredClusters.length === 0 && searchQuery === "" ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <SiKubernetes className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No Cluster Access</h3>
                <p className="text-sm text-muted-foreground max-w-md mb-6">
                  You currently don't have access to any Kubernetes clusters. Please request cluster access from the Resources page first. Once your request is approved, return here to request access to specific cluster resources.
                </p>
                {/**
                 * <Button variant="outline" onClick={() => navigate("/aws/users")} data-testid="button-go-to-resources">
                  Go to Resources
                </Button>
                 */}

              </CardContent>
            </Card>) : paginatedClusters.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No clusters found</p>
              </div>
            ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {paginatedClusters.map((cluster) => {


                  return (
                    <Card
                      key={cluster.id}
                      className="hover-elevate active-elevate-2 cursor-pointer transition-all"
                      onClick={() => setSelectedCluster(cluster.id)}
                      data-testid={`card-cluster-${cluster.id}`}
                    >
                      <CardHeader className="pb-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <SiKubernetes className="w-8 h-8 text-primary" />
                          </div>
                          {cluster.type !== '-' && (
                            <Badge variant="secondary" className="text-xs">
                              {cluster.type}
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <h3 className="font-semibold text-lg mb-1 break-all">
                            {cluster.name}
                          </h3>
                          {cluster.groupName !== '-' && (
                            <p className="text-sm text-muted-foreground">
                              {cluster.groupName} Environment
                            </p>
                          )}
                        </div>

                        <div className="space-y-2 pt-2 border-t">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Region</span>
                            <span className="font-medium">{cluster.region}</span>
                          </div>

                        </div>

                        <Button
                          className="w-full gap-2 text-primary hover:text-primary"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCluster(cluster.id);
                          }}
                          data-testid={`button-cluster-${cluster.id}`}
                        >
                          View Resources
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Cluster Pagination */}
              {totalClusterPages > 1 && (
                <div className="flex items-center justify-between border-t pt-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {clusterStartIndex + 1}-{Math.min(clusterStartIndex + ITEMS_PER_PAGE, filteredClusters.length)} of {filteredClusters.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setClusterPage(p => Math.max(1, p - 1))}
                      disabled={clusterPage === 1}
                      data-testid="button-cluster-prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {clusterPage} of {totalClusterPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setClusterPage(p => Math.min(totalClusterPages, p + 1))}
                      disabled={clusterPage === totalClusterPages}
                      data-testid="button-cluster-next-page"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Cluster Detail View (Inside a cluster)
  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header with back button */}
      <div className="px-6 py-4 border-b bg-card">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedCluster(null);
              setCurrentPage(1);
              setResourceSearchQuery("");
              setTypeSearchQuery("");
            }}
            className="gap-2"
            data-testid="button-back-to-clusters"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Clusters
          </Button>
          <div className="flex items-center gap-2">
            <SiKubernetes className="w-6 h-6 text-primary" />
            <h2 className="font-semibold text-lg">{currentCluster?.name}</h2>
            <span className="text-sm text-muted-foreground">({currentCluster?.region})</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Resource Types Sidebar */}
        <div className="w-64 border-r flex flex-col bg-card flex-shrink-0">
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search types..."
                value={typeSearchQuery}
                onChange={(e) => setTypeSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="input-search-types"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto p-2">
            <div className="space-y-1">
              {filteredResourceTypes?.map((type) => {
                const isSelected = selectedResourceType === type.key;
                const Icon = type.icon;
                return (
                  <button
                    key={type.key}
                    onClick={() => handleResourceTypeChange(type.key)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${isSelected
                      ? "bg-primary text-primary-foreground"
                      : "hover-elevate active-elevate-2"
                      }`}
                    data-testid={`button-type-${type.key}`}
                  >

                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="font-medium truncate">{type.value}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Resources Content */}
        <div className="flex-1 flex flex-col">
          <div className="px-6 py-4 border-b bg-card/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">
                  {resourceTypes.find(t => t.id === selectedResourceType)?.name || "Resources"}
                </h3>
                <span className="text-sm text-muted-foreground">
                  ({searchFilteredResources.length} {searchFilteredResources.length === 1 ? 'item' : 'items'})
                </span>
              </div>
            </div>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search resources..."
                value={resourceSearchQuery}
                onChange={(e) => handleResourceSearch(e.target.value)}
                className="pl-10"
                data-testid="input-search-resources"
              />
            </div>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-6">
              {paginatedResources.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    < Card className="border-dashed">
                      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                          <SiKubernetes className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2">No Published Resource Available</h3>
                        <p className="text-sm text-muted-foreground max-w-md mb-6">
                          You currently don't have published resource to access.
                        </p>

                      </CardContent>
                    </Card>
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {!resourceLoading && paginatedResources.length > 0 && paginatedResources.map((resource) => {
                      const typeInfo = resourceTypes.find(t => t.id === resource.type) || resourceTypes[0];
                      const Icon = typeInfo.icon;
                      const status = selectedResourceType.toLowerCase().includes("node")
                        || selectedResourceType.toLowerCase().includes("deployement")
                        || selectedResourceType.toLowerCase().includes("pod") ? "running" : "available"
                      const isHasAccess = fetchAccessGranted(selectedResourceType || "", resource);
                      return (
                        <Card
                          key={resource.id}
                          className="hover-elevate transition-all flex flex-col"
                          data-testid={`card-resource-${resource.id}`}
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Icon className="w-6 h-6 text-primary" />
                              </div>
                              <Badge
                                variant="secondary"
                                className={`text-xs ${getStatusColor(status)}`}
                              >
                                {status}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent className="flex flex-col flex-1">
                            <div className="flex-1 space-y-3">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-medium text-sm break-all flex-1" title={resource.name}>
                                    {resource.name}
                                  </h3>
                                  <DetailsDialog
                                    data={resource}
                                    title={`${resource.name} Details`}
                                    trigger="icon"
                                    testId={`button-details-${resource.id}`}
                                  />
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {resource.namespace || "Cluster-wide"}
                                </p>
                              </div>

                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between gap-2">
                                  <span className="text-muted-foreground">Cluster:</span>
                                  <span className="font-medium truncate">{resource.cluster}</span>
                                </div>
                                {resource.namespace && (
                                  <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground">Namespace:</span>
                                    <span className="font-medium truncate">{resource.namespace}</span>
                                  </div>
                                )}
                                <div className="flex justify-between gap-2">
                                  <span className="text-muted-foreground">Created:</span>
                                  <span className="font-medium">{resource.createdAt}</span>
                                </div>
                              </div>
                            </div>

                            <div className="mt-4">



                              {isHasAccess && isHasAccess.length > 0 ? (
                                <div className="flex items-center justify-center gap-2 p-3 rounded-md bg-green-500/10 border border-green-500/20">
                                  <Clock className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  <div className="flex flex-col items-center">
                                    <span className="text-xs text-muted-foreground">Access Granted</span>
                                    <span className="text-sm font-semibold text-green-600 dark:text-green-400 font-mono">
                                      {formatTime(isHasAccess, "kubernetes")}
                                    </span>
                                  </div>
                                </div>
                              ) :

                                <Button
                                  className="w-full gap-2"
                                  size="sm"
                                  onClick={() => {
                                    setRequestAccessResource(resource);
                                    setRequestAccessDialogOpen(true);
                                  }}
                                  data-testid={`button-request-${resource.id}`}
                                >
                                  Request Access
                                  <ChevronRight className="w-4 h-4" />
                                </Button>
                              }
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, searchFilteredResources.length)} of {searchFilteredResources.length}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          data-testid="button-prev-page"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>
                        <div className="text-sm">
                          Page {currentPage} of {totalPages}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          data-testid="button-next-page"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Request Access Dialog */}
      <RequestCloudAccessDialog
        open={requestAccessDialogOpen}
        onOpenChange={setRequestAccessDialogOpen}
        resourceName={requestAccessResource?.name || ""}
        resourceType="kubernetes"
        resourceTypeLabel={requestAccessResource ? getResourceTypeLabel(requestAccessResource.type) : undefined}
        availableRoles={availableRoles}
        resource={requestAccessResource}
        upn={upn || ""}
        displayName={displayName || ""}
        clustername={currentCluster.name}
        type={selectedResourceType}
      />
    </div>
  );
}
